import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  type ModalProps,
  Stack,
  Text,
} from '@chakra-ui/react';
import { IconCheckmark2Small } from 'central-icons/IconCheckmark2Small';
import { IconCircleInfo } from 'central-icons/IconCircleInfo';
import { useEffect, useState } from 'react';

import { CenterLoading } from '@revelio/core';
import { Badge, BadgeColor, NavigationButton } from '@revelio/ui';

import { DataProviderQueryStatus } from '../../next/CustomDash/components/DataProvider/types';
import { usePreferenceToast } from './hooks/usePreferenceToast';
import type {
  OnPreferenceUpdateCallback,
  preferenceEditors,
} from './preference-editors/preferenceEditors';
import styles from './preferences-modal.module.css';
import {
  PreferenceScope,
  UserPreferenceKey,
  type UserPreferences,
} from './types';
import { UserPreferencesDataProvider } from './UserPreferencesDataProvider';
import { calculateVisiblePreferenceDiff } from './utils/preference-diff';
import {
  restoreVisiblePreferencesToDefaults,
  saveVisiblePreferencesAsDefault,
} from './utils/preference-operations';
import {
  getVisiblePreferenceEditors,
  getVisiblePreferenceKeys,
} from './utils/visibility';

/** Convenience types derived from registry */
type PreferenceEntry = (typeof preferenceEditors)[number];
type PreferenceId = PreferenceEntry['meta']['id'];

interface PreferencesModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  preferencesFromFilters?: Partial<UserPreferences>;
  onPreferenceUpdate: OnPreferenceUpdateCallback;
  affectedScope: PreferenceScope;
  preferenceVisibility?: (key: UserPreferenceKey) => boolean;
}

export const PreferencesModal = ({
  affectedScope,
  onPreferenceUpdate,
  preferencesFromFilters,
  preferenceVisibility,
  ...modalProps
}: PreferencesModalProps) => {
  const [activeId, setActiveId] = useState<PreferenceId>();
  const [isSaving, setIsSaving] = useState(false);
  const showToast = usePreferenceToast();

  const effectivePreferenceVisibility = (key: UserPreferenceKey): boolean => {
    if (
      affectedScope === PreferenceScope.INDIVIDUAL &&
      key === UserPreferenceKey.DefaultFilters
    ) {
      return false;
    }
    return preferenceVisibility ? preferenceVisibility(key) : true;
  };

  const relevantEditors = getVisiblePreferenceEditors(
    effectivePreferenceVisibility
  );
  const visiblePreferenceKeys = getVisiblePreferenceKeys(
    effectivePreferenceVisibility
  );
  const activeEntry =
    relevantEditors.find((e) => e.meta.id === activeId) ?? relevantEditors[0];
  const ActiveEditorComponent = activeEntry?.component;

  useEffect(() => {
    setActiveId(activeEntry?.meta.id ?? undefined);
  }, [activeEntry]);

  // Handle setting current preferences as default
  const handleSetAsDefault = async (
    effectivePreferences: UserPreferences,
    refetchSavedPreferences: () => Promise<void>
  ) => {
    try {
      setIsSaving(true);
      await saveVisiblePreferencesAsDefault(
        affectedScope,
        effectivePreferences,
        visiblePreferenceKeys,
        refetchSavedPreferences
      );
      showToast('Preferences saved', 'success', {
        description: 'Your current preferences have been set as default.',
      });
    } catch (err) {
      showToast('Failed to save preferences', 'error', { error: err });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle resetting to defaults
  const handleRestoreDefaults = async (savedDefaults: UserPreferences) => {
    try {
      const { appliedDefaults } = restoreVisiblePreferencesToDefaults(
        affectedScope,
        savedDefaults,
        visiblePreferenceKeys
      );

      // Update local filters to reflect restored defaults for visible preferences only
      visiblePreferenceKeys.forEach((key) => {
        const value = appliedDefaults[key];
        onPreferenceUpdate(key as UserPreferenceKey, value);
      });

      showToast('Preferences restored', 'success', {
        description: 'Your preferences have been restored to defaults.',
      });
    } catch (err) {
      showToast('Failed to reset preferences', 'error', { error: err });
    }
  };

  return (
    <UserPreferencesDataProvider
      affectedScope={affectedScope}
      preferencesFromFilters={preferencesFromFilters}
    >
      {({ data, status }) => {
        if (!data) return null;
        const { preferences, savedPreferences, refetchSavedPreferences } = data;
        if (!preferences || !savedPreferences) return null;
        const hasError = status === DataProviderQueryStatus.Error;
        const diff = calculateVisiblePreferenceDiff(
          preferences,
          savedPreferences,
          visiblePreferenceKeys,
          hasError
        );

        return (
          <Modal size="2xl" isCentered {...modalProps}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                <Text fontSize="lg" fontWeight="semibold" mb={2}>
                  Preferences
                </Text>
                {(() => {
                  if (hasError) {
                    return (
                      <Flex alignItems="center" color="orange.500">
                        <Icon as={IconCircleInfo} boxSize={4} />
                        <Text fontSize="sm" ml={2}>
                          We couldn't load your saved defaults. You can still
                          adjust preferences locally.
                        </Text>
                      </Flex>
                    );
                  }
                  if (diff.length === 0) {
                    return (
                      <HStack spacing={1} alignItems="center">
                        <Icon
                          as={IconCheckmark2Small}
                          color="text.primary"
                          boxSize={4}
                        />
                        <Text fontSize="sm" fontWeight="normal">
                          Dashboard settings match your default settings
                        </Text>
                      </HStack>
                    );
                  }

                  const effectiveDiff = diff.filter(
                    (d) => d !== UserPreferenceKey.Taxonomies
                  );
                  const differenceText =
                    effectiveDiff.length === 1
                      ? '1 setting differs from your account defaults:'
                      : `${effectiveDiff.length} settings differ from your account defaults:`;
                  return (
                    <Flex alignItems="center">
                      <HStack>
                        <Badge size="sm" />
                        <Text fontSize="sm" fontWeight="normal">
                          {differenceText}
                        </Text>
                      </HStack>

                      <HStack spacing={1} fontSize="sm" ml={2} mr={1}>
                        <Button
                          data-testid="preferences-set-as-default"
                          variant="link"
                          color="lightBlue.350"
                          onClick={() =>
                            handleSetAsDefault(
                              preferences,
                              refetchSavedPreferences
                            )
                          }
                          isLoading={isSaving}
                          isDisabled={isSaving}
                        >
                          Set as default
                        </Button>
                        <Text color="gray.400">/</Text>
                        <Button
                          data-testid="preferences-restore-defaults"
                          variant="link"
                          color="lightBlue.350"
                          onClick={() =>
                            handleRestoreDefaults(savedPreferences)
                          }
                        >
                          Restore defaults
                        </Button>
                      </HStack>
                    </Flex>
                  );
                })()}
              </ModalHeader>
              <ModalCloseButton />
              <Divider />
              <ModalBody p={0}>
                {status === DataProviderQueryStatus.Fetching && (
                  <Box
                    height="280px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <CenterLoading />
                  </Box>
                )}
                {status !== DataProviderQueryStatus.Fetching && (
                  <Flex align="stretch" minH={80}>
                    {/* Sidebar of categories */}
                    <Box
                      minW="270px"
                      p={4}
                      borderRight="1px"
                      borderColor="gray.200"
                      data-testid="user-preferences-sidebar"
                    >
                      <Stack spacing={2}>
                        {relevantEditors.map(({ meta }) => {
                          const hasDifference =
                            !hasError && diff.includes(meta.id);
                          return (
                            <NavigationButton
                              key={meta.id}
                              className={styles.navigationMenuItem}
                              isSelected={meta.id === activeId}
                              size="sm"
                              slot={
                                hasDifference && (
                                  <Badge
                                    className={
                                      styles.navigationMenuDiffIndicator
                                    }
                                    size="sm"
                                    color={
                                      meta.id === activeId
                                        ? BadgeColor.WHITE
                                        : BadgeColor.DEFAULT
                                    }
                                    style={{
                                      marginLeft: '0.25rem',
                                      marginRight: '0.25rem',
                                    }}
                                  />
                                )
                              }
                              onClick={() => setActiveId(meta.id)}
                            >
                              {meta.label}
                            </NavigationButton>
                          );
                        })}
                        {relevantEditors.length === 0 && (
                          <Text>No preferences available</Text>
                        )}
                      </Stack>
                    </Box>

                    {/* Active editor */}
                    <Box flex="1" p={4}>
                      {ActiveEditorComponent && (
                        <ActiveEditorComponent
                          affectedScope={affectedScope}
                          onPreferenceUpdate={onPreferenceUpdate}
                          preferences={preferences}
                          diff={diff}
                        />
                      )}
                    </Box>
                  </Flex>
                )}
              </ModalBody>
            </ModalContent>
          </Modal>
        );
      }}
    </UserPreferencesDataProvider>
  );
};
